<template>
  <section class="proposals-read-information">
    <information-accordion
      :sections="sections"
      class="proposals-read-information-accordion"
      collapsed
    >
      <template v-slot:[`${proposalSection._ID}-${SELLER_KEY}-value`]>
        <proposals-read-seller
          :proposal="proposal"
        />
      </template>

      <template v-slot:[`${proposalSection._ID}-${OWNER_KEY}-value`]>
        <proposals-read-owner
          :proposal="proposal"
        />
      </template>

      <template v-slot:[customerSection._ID]>
        <context-loading v-if="loading" />

        <template v-else>
          <customers-actions />

          <customers-summary collapsed />
        </template>
      </template>

      <template v-slot:[`${customerSection._ID}-subtitle`]>
        <!-- <customers-alert /> -->
      </template>

      <template v-slot:[`${proposalSection._ID}-subtitle`]>
        <context-tag :color="style(proposal.proposal_attempt)">
          {{ proposal.proposal_attempt && proposal.proposal_attempt.state
            ? status(proposal.proposal_attempt.state) : '' }}
        </context-tag>

        <context-tag
          v-if="tagged(proposal)"
          color="external"
          class="proposals-information-online"
          :icon="['fas', 'right-to-bracket']"
        >
          Via {{ tagged(proposal) }}
        </context-tag>
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-${NUMBER_KEY}-value`]
      >
        <template v-if="outdated(attempts[attempt._ID])">
          <context-tag
            :key="`${attempt._ID}-${NUMBER_KEY}`"
            :icon="['fa', 'fa-exclamation-circle']"
            class="proposals-read-label-outdated"
          >
            desatualizado
          </context-tag>
        </template>
        <template
          v-else-if="replaces(attempts[attempt._ID])"
        >
          <context-tag
            :key="`${attempt._ID}-${NUMBER_KEY}`"
            :icon="['fa', 'fa-rotate']"
            class="proposals-read-label-replaces"
          >
            banco atualizou
          </context-tag>
        </template>
        #{{ attempts[attempt._ID].id }}
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-${INSTALLMENT_KEY}-value`]
      >
        <template v-if="outdated(attempts[attempt._ID])">
          <context-tag
            :key="`${attempt._ID}-${INSTALLMENT_KEY}`"
            :icon="['fa', 'fa-exclamation-circle']"
            class="proposals-read-label-outdated"
          >
            desatualizado
          </context-tag>
        </template>
        <template
          v-else-if="replaces(attempts[attempt._ID])"
        >
          <context-tag
            :key="`${attempt._ID}-${INSTALLMENT_KEY}`"
            :icon="['fa', 'fa-rotate']"
            class="proposals-read-label-replaces"
          >
            banco atualizou
          </context-tag>
        </template>
        <span
          v-for="installment in getInstallments(attempts[attempt._ID])"
          :key="installment.value"
        >{{ installment.value }}</span>
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-${PAYMENT_KEY}-label`]
      >
        <span
          v-if="attempts[attempt._ID].payment_type
            && attempts[attempt._ID].payment_type.name === 'Débito em conta'"
          :key="`${attempt._ID}-${PAYMENT_KEY}`"
        >
          Forma de pagamento

          &nbsp;

          <context-tag>Importante</context-tag>
        </span>
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-${CODE_KEY}-value`]
      >
        <proposals-read-code
          v-if="allowed"
          :key="`${attempt._ID}-${CODE_KEY}`"
          :proposal="proposal"
          :attempt="attempts[attempt._ID]"
        />
      </template>

      <template
        v-for="commission in commissionAttempts"
        v-slot:[`${commission.attempt_id}-COMMISSION-${commission.type_identifier}-value`]
      >
        <proposals-read-commission
          v-if="allowed"
          :key="`${commission.attempt_id}-COMMISSION-${commission.type_identifier}`"
          :proposal="proposal"
          :attempt="attempts[commission.attempt_id]"
          :type="commission.type_identifier"
        />
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-${NOTE_KEY}-value`]
      >
        <proposals-read-note
          v-if="allowed"
          :key="`${attempt._ID}-${NOTE_KEY}`"
          :proposal="proposal"
          :attempt="attempts[attempt._ID]"
        />
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-subtitle`]
      >
        <span
          :key="`${attempt._ID}-information`"
          class="proposals-read-information-updated"
        >
          <template v-if="outdated(attempts[attempt._ID])">
            <b class="proposals-read-information-outdated">
              Proposta desatualizada,
            </b>
            tentativa atualizada em
            {{ attempts[attempt._ID].updated_at | datetime }}
          </template>

          <template v-else-if="integrated(attempts[attempt._ID])">
            <b class="proposals-read-information-integrated">
              Proposta integrada,
            </b>
            tentativa atualizada em
            {{ attempts[attempt._ID].updated_at | datetime }}
          </template>

          <template v-else>
            <b class="proposals-read-information-non-integrated">
              Proposta não integrada,
            </b>
            status atualizado manualmente
          </template>
        </span>

        <context-tag
          v-if="!integrated(attempts[attempt._ID]) || !approved(attempts[attempt._ID])"
          :key="`${attempt._ID}-subtitle-status`"
          :color="style(attempts[attempt._ID])"
          class="proposals-read-information-status"
        >
          {{ status(attempts[attempt._ID].state) }}
        </context-tag>

        <context-tag
          v-if="isUnallowed(attempt)"
          :key="`${attempt._ID}-subtitle-unallowed`"
          class="proposals-read-information-status"
          icon="triangle-exclamation"
          color="warning"
        >
          Regra comercial
        </context-tag>

        <context-tag
          v-if="outdated(attempts[attempt._ID])"
          :key="`${attempt._ID}-subtitle-outdated`"
          class="proposals-read-information-status"
        >
          Desatualizada
        </context-tag>

        <context-tag
          v-if="origined(attempts[attempt._ID])"
          :key="`${attempt._ID}-subtitle-origin`"
          :icon="['fas', 'right-to-bracket']"
          color="external"
          class="proposals-read-information-status"
        >
          Via {{ origined(attempts[attempt._ID]) }}
        </context-tag>

        <context-alert
          v-if="integrated(attempts[attempt._ID]) && approved(attempts[attempt._ID])"
          :key="`${attempt._ID}-approved`"
          class="proposals-read-information-approved"
          color="success"
          self
        >
          Condição aprovada. Fature agora!

          <template v-slot:action>
            <proposals-approved-button
              :user="auth.user"
              :proposal="proposal"
              :attempt="attempts[attempt._ID]"
              color="highlight"
            />
          </template>
        </context-alert>

        <context-alert
          v-if="integrationError(attempts[attempt._ID])"
          :key="`${attempt._ID}-error`"
          class="proposals-read-information-error"
        >
          <span class="proposals-read-information-error-details">
            <b>{{ integrationErrorText(attempts[attempt._ID]).message }}</b>

            <template v-if="integrationErrorDetail(attempts[attempt._ID])">
              {{ integrationErrorText(attempts[attempt._ID]).error_details }}
            </template>

            <template
              v-if="bankAccounts && bankAccountsAttempt && bankAccountsAttempt.id === attempt._ID"
            >
              <br><br>

              {{
                bankAccounts.length === 1
                  ? 'A conta bancária abaixo foi a sugerida pelo banco:'
                  : 'As contas bancárias abaixo foram as sugeridas pelo banco:'
              }}

              <ul class="proposals-read-information-banks-list">
                <li
                  v-for="account in bankAccounts"
                  :key="account.id"
                  class="proposals-read-information-banks-item"
                >
                  Agência <b>{{ account.agencyCode }}</b> e
                  Conta <b>{{ account.accountCode }}-{{ account.accountDigit }}</b>
                </li>
              </ul>
            </template>
          </span>
        </context-alert>
      </template>

      <template
        v-for="attempt in proposalAttemptsSection.recursive"
        v-slot:[`${attempt._ID}-subheader`]
      >
        <context-alert
          v-if="draft(attempts[attempt._ID])"
          :key="`${attempt._ID}-draft`"
          class="proposals-read-information-draft"
          color="info"
        >
          <b>Esta tentativa é um rascunho.</b> Você poderá atualizar o status dela (manualmente)
          quando definir o banco para o qual ela foi enviada.
        </context-alert>

        <information-line
          v-if="allowed"
          :key="`${attempt._ID}-status`"
          label="Status atual"
          :outdated="outdated(attempts[attempt._ID])"
        >
          <proposals-read-status
            :proposal="proposal"
            :attempt="attempts[attempt._ID]"
            :customer="customer"
          />
        </information-line>

        <information-line
          v-if="editable(attempts[attempt._ID]) && !outdated(attempts[attempt._ID])"
          :key="`${attempt._ID}-edit`"
        >
          <form-button
            icon="pen"
            :href="edit(attempts[attempt._ID])"
          >
            {{ draft(attempts[attempt._ID]) ? 'Definir banco' : 'Editar tentativa' }}
          </form-button>
        </information-line>
      </template>
    </information-accordion>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import origin from '@sdk/helpers/interface/origin';

  import namelize from '@sdk/helpers/formatters/namelize';
  import momentize, { DATETIME_FORMAT } from '@sdk/helpers/formatters/momentize';
  import monetize from '@sdk/helpers/formatters/monetize';
  import vehicleize from '@sdk/helpers/formatters/vehicleize';

  import identify, {
    SPF,
    SPF_SAP,
    SPF_WARRANTY,
    SPF_ASSISTANCE,
    CAP,
    BEM,
    SAP,
  } from '@sdk/helpers/misc/expenses';

  import {
    insurances,
    interests,
    installments,
    integrated,
    table,
    plan,
  } from '@sdk/helpers/misc/installments';

  import status, { id } from '@cordel/helpers/colors/proposals';

  import ContextAlert from '@cordel/components/Context/Alert.vue';

  import CustomersActions from '@sdk/views/Customers/Actions.vue';
  // import CustomersAlert from '@sdk/views/Customers/Alert.vue';
  import CustomersSummary from '@sdk/views/Customers/Summary.vue';

  import ProposalsReadNote from '@sdk/views/Proposals/Read/Note.vue';
  import ProposalsReadCode from '@sdk/views/Proposals/Read/Code.vue';
  import ProposalsReadCommission from '@sdk/views/Proposals/Read/Commission.vue';
  import ProposalsReadOwner from '@sdk/views/Proposals/Read/Owner.vue';
  import ProposalsReadSeller from '@sdk/views/Proposals/Read/Seller.vue';
  import ProposalsReadStatus from '@sdk/views/Proposals/Read/Status.vue';

  import ProposalsApprovedButton from '@sdk/views/Proposals/Approved/Button.vue';

  import ContextLoading from '@cordel/components/Context/Loading.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';

  import InformationAccordion from '@cordel/components/Information/Accordion.vue';
  import InformationLine from '@cordel/components/Information/Line.vue';
  import FormButton from '@cordel/components/Form/Button.vue';

  import BANKS from '@sdk/helpers/domains/banks';
  import unallowed, { UNALLOWED, isUnallowed } from '@sdk/helpers/misc/unallowed';

  import CONSTANTS from './Information/constants';

  const NUMBER_KEY = 'number';
  const INSTALLMENT_KEY = 'installment';
  const PAYMENT_KEY = 'payment';
  const NOTE_KEY = 'note';
  const CODE_KEY = 'code';
  const SELLER_KEY = 'seller';
  const OWNER_KEY = 'owner';

  const ITAU_ACCOUNT_ERROR = /A conta bancária informada para pagamento no débito automático é inválida/;

  export default {
    name: 'ProposalsReadInformation',
    components: {
      ContextAlert,

      CustomersActions,
      // CustomersAlert,
      CustomersSummary,

      ProposalsReadNote,
      ProposalsReadCode,
      ProposalsReadCommission,
      ProposalsReadOwner,
      ProposalsReadSeller,
      ProposalsReadStatus,

      ProposalsApprovedButton,

      ContextLoading,
      ContextTag,

      InformationAccordion,
      InformationLine,
      FormButton,
    },
    data() {
      return {
        status,

        NUMBER_KEY,
        INSTALLMENT_KEY,
        PAYMENT_KEY,
        NOTE_KEY,
        CODE_KEY,
        SELLER_KEY,
        OWNER_KEY,
      };
    },
    computed: {
      ...mapState([
        'auth',
        'customers',
        'proposals',
        'proposalAttempts',
        'simulations',
        'domainsWeb',
        'financingReturnRules',
        'financingProductRules',
      ]),

      proposal() {
        return this.proposals.read.item;
      },
      attempts() {
        return this.proposalAttempts.list.items;
      },

      rules() {
        return this.financingReturnRules.findForIntegratedBanks.result
          .concat(this.financingProductRules.findForIntegratedBanks.result);
      },
      unallowed() {
        return Object
          .values(this.attempts)
          .reduce((reduced, attempt) => Object.assign(reduced, {
            [attempt.id]: this.integrated(attempt) ? unallowed({
              settings: {
                _enable_commercial_control_unallowed_tags: true,
              },
              rules: this.rules,
            }, {
              id: attempt.id,
              credit_condition_return: attempt.plan && attempt.plan.return,
              include_financial_protection_insurance: attempt
                .expenses
                .some(e => [SPF, SPF_SAP, SPF_WARRANTY, SPF_ASSISTANCE].includes(identify(e, false))),
              products_options: {
                include_asset_insurance: attempt
                  .expenses
                  .some(e => identify(e, false) === BEM),
                include_personal_insurance: attempt
                  .expenses
                  .some(e => [SAP, SPF_SAP].includes(identify(e, false))),
                include_capitalization_bond: attempt
                  .expenses
                  .some(e => identify(e, false) === CAP),
              },
            }) : {},
          }), {});
      },

      simulation() {
        return this.simulations.read.item;
      },
      simulationLoading() {
        return this.simulations.read.loading;
      },
      simulationError() {
        return this.simulations.read.error;
      },

      customer() {
        return this.customers.read.item;
      },
      loading() {
        return this.customers.read.loading;
      },

      allowed() {
        if (!this.auth.user || !this.auth.user.role || !this.proposal || !this.proposal.store) {
          return false;
        }

        return this.auth.user.role.identifier !== 'salesman'
          || (this.proposal.store && this.proposal.store.seller_can_send_proposal_to_bank);
      },

      vehicleFields() {
        if (this.proposal.zero_km) {
          return [];
        }

        return [{
          label: 'Chassi',
          value: this.proposal.chassi_code,
        }, {
          label: 'Cor',
          value: namelize(this.proposal.color) || null,
        }, {
          label: 'Placa',
          value: this.proposal.license_plate_code,
        }, {
          label: 'Renavam',
          value: this.proposal.renavam_code,
        }, {
          label: 'Kilômetros',
          value: this.proposal.km_mileage,
        }];
      },

      proposalAttemptsSection() {
        const attempts = Object.values(this.attempts);

        if (!attempts.length) {
          return {
            recursive: [],
          };
        }

        return {
          _ID: 'proposalAttempts',
          _COLLAPSED: false,
          title: 'Tentativas por banco',
          subtitle: 'Informações do envio da proposta',
          recursive: attempts
            .map(this.detail)
            // eslint-disable-next-line no-underscore-dangle
            .sort((a, b) => this.attempts[a._ID].state_rank - this.attempts[b._ID].state_rank),
        };
      },
      customerSection() {
        if (!this.customer.id) {
          return {};
        }

        return {
          _ID: 'customer',
          title: this.customer.id ? namelize(this.customer.name) : 'Cliente',
          subtitle: 'Dados do(a) cliente',
        };
      },
      proposalSection() {
        if (!this.proposal.id) {
          return {};
        }

        let { store, seller } = this.proposal;

        if (!store) {
          store = { name: 'Loja desconhecida' };
        }

        if (!seller) {
          seller = { name: 'Vendedor desconhecido' };
        }

        const fields = [{
          label: 'Loja',
          value: store.name,
          group: 'scope',
        }, {
          _ID: SELLER_KEY,
          label: 'Vendedor(a)',
          value: namelize(seller.name),
          group: 'scope',
        }];

        if (
          ['credit', 'store_manager', 'bank_operator'].includes(this.auth.user.role.identifier)
          && (!this.proposal.owner || this.proposal.owner.id === this.auth.user.id)
        ) {
          fields.push({
            _ID: OWNER_KEY,
            label: 'Assumir responsabilidade da proposta',
            value: null,
            group: 'scope',
          });
        }

        const vehicle = [{
          label: 'Veículo',
          value: vehicleize(this.proposal.vehicle_model || {}),
        }, {
          label: 'Veículo novo ou usado?',
          value: this.proposal.zero_km ? 'Novo' : 'Usado',
        }, {
          label: 'Veículo para uso comercial?',
          value: this.proposal.commercial,
        }, {
          label: 'Ano do veículo',
          value: `${this.proposal.year_of_manufacture}/${this.proposal.year_of_model}`,
        }, {
          label: 'Combustível',
          value: this.proposal.fuel ? this.proposal.fuel.name.toUpperCase() : null,
        }];

        fields.push({
          label: 'Responsável pela proposta',
          value: this.proposal.owner ? namelize(this.proposal.owner.name) : null,
          group: 'scope',
        }, {
          label: 'Número da proposta',
          value: `#${this.proposal.id}`,
        }, {
          label: 'Cadastrada em',
          value: momentize(this.proposal.created_at, { output: DATETIME_FORMAT }),
        }, ...vehicle);

        return {
          _ID: 'proposal',
          _COLLAPSED: false,
          title: 'Proposta',
          fields: fields.concat(this.vehicleFields),
        };
      },

      sections() {
        if (!this.proposal.id) {
          return [];
        }

        return [
          this.proposalSection,
          this.customerSection,
          this.proposalAttemptsSection,
        ];
      },

      bankAccountsAttempt() {
        return Object.values(this.attempts).find(i => this.integrationError(i)
          && ITAU_ACCOUNT_ERROR.test(this.integrationErrorText(i).error_details));
      },
      bankAccounts() {
        if (!this.simulation.uuid
          || !this.bankAccountsAttempt
          || this.simulationLoading
          || this.simulationError) {
          return null;
        }

        const payload = this.simulation.conditions
          .find(i => i.id === this.bankAccountsAttempt.simulation_condition_id)
          .process_condition_payload;

        if (payload && payload.debitAccounts && payload.debitAccounts.length) {
          return payload.debitAccounts;
        }

        return null;
      },

      commissionAttempts() {
        const list = [];

        this.proposalAttemptsSection.recursive.forEach((attempt) => {
          (this.domainsWeb.list.items.commission_type || []).forEach(type => list.push({
            // eslint-disable-next-line no-underscore-dangle
            attempt_id: attempt._ID,
            type_identifier: type.identifier,
          }));
        });

        return list;
      },
    },
    mounted() {
      if (this.bankAccountsAttempt && this.proposal.store) {
        this.$store.dispatch('simulations/read', {
          uuid: this.bankAccountsAttempt.external_simulation_uuid,
          header_store_id: this.proposal.store.id,
        });
      }
    },
    methods: {
      editable(attempt) {
        if (!this.allowed) {
          return false;
        }

        return !attempt.external_proposal_uuid;
      },
      edit(attempt) {
        if (attempt.external_simulation_uuid) {
          return {
            name: 'simulations-read',
            params: {
              uuid: attempt.external_simulation_uuid,
            },
            query: {
              store_id: this.proposal.store.id,

              proposal_id: this.$route.params.id,
              proposal_attempt_id: attempt.id,
              focus_condition_id: attempt.simulation_condition_id,
            },
          };
        }

        return {
          name: 'proposal-attempts-edit',
          params: {
            proposal_id: this.$route.params.id,
            proposal_attempt_id: attempt.id,
          },
          query: {
            integrated: false,
          },
        };
      },

      integrated(attempt) {
        return integrated(attempt, this.customer);
      },
      approved(attempt) {
        return attempt.state === 'aprovada';
      },
      outdated(attempt) {
        return attempt.replaced_by_proposal_attempt_id;
      },
      origined(attempt) {
        return origin(attempt);
      },
      replaces(attempt) {
        return attempt.replaces_proposal_attempt_id;
      },
      draft(attempt) {
        return attempt.bank && attempt.bank.name === CONSTANTS.BANKS.DRAFT;
      },

      getInstallments(attempt) {
        return installments(attempt);
      },

      style(attempt) {
        if (!attempt || !attempt.state || !Object.prototype.hasOwnProperty.call(attempt, 'state')) {
          return '';
        }
        return `status-${id(attempt.state)}`;
      },

      integrationError(attempt) {
        return attempt && attempt.integration_error && attempt.integration_error.error;
      },
      integrationErrorDetail(attempt) {
        const error = this.integrationErrorText(attempt);

        return error.error !== 'banks_api_error' && !!error.error_details;
      },
      integrationErrorText(attempt) {
        return attempt.integration_error;
      },

      detail(attempt) {
        const details = {
          installments: installments(attempt).map(field => ({
            ...field,
            _ID: INSTALLMENT_KEY,
            labelClass: this.outdated(attempt) ? 'outdated-label' : '',
          })),
          interests: interests(attempt).map(field => ({
            ...field,
            labelClass: this.outdated(attempt) ? 'outdated-label' : '',
          })),
        };

        if (attempt.bank && attempt.bank.febraban_code === BANKS.SAFRA.code) {
          details.insurances = insurances({
            ...attempt,
            expenses: attempt.expenses.filter(e => e.value_in_cents),
          }).map(field => ({
            ...field,
            labelClass: this.outdated(attempt) ? 'outdated-label' : '',
          }));
        } else {
          details.insurances = insurances(attempt).map(field => ({
            ...field,
            labelClass: this.outdated(attempt) ? 'outdated-label' : '',
          }));
        }

        const isOutdated = this.outdated(attempt);

        const fields = [{
          _ID: NUMBER_KEY,
          label: 'Número da tentativa',
          group: 'header',
          separator: true,
        }, {
          label: 'Cadastrada em',
          group: 'header',
          value: momentize(attempt.created_at, { output: DATETIME_FORMAT }),
        }];

        let planName = plan(attempt.plan);

        if (true
          && !['', 'Em branco', undefined, null].includes(planName)
          && typeof attempt.return_value_cents === 'number'
        ) {
          planName += ` (${monetize(attempt.return_value_cents)})`;
        }

        fields.push({
          group: 'table',
          label: 'Retorno',
          value: planName,
          tag: this.unallowed[attempt.id] && this.unallowed[attempt.id].credit_condition_return
            ? {
              label: this.unallowed[attempt.id].credit_condition_return,
              color: 'warning',
              icon: 'triangle-exclamation',
            } : undefined,
        });

        // eslint-disable-next-line no-underscore-dangle
        const _integrated = integrated(attempt);

        if (_integrated) {
          fields.push({
            group: 'table',
            label: 'Tabela',
            value: table(attempt.table),
          });

          if (this.unallowed[attempt.id].include_financial_protection_insurance) {
            fields.push({
              label: SPF,
              value: undefined,
              tag: {
                label: UNALLOWED.include_financial_protection_insurance(true, false),
                color: 'warning',
                icon: 'triangle-exclamation',
              },
            });
          }

          if (attempt.payment_type) {
            fields.push({
              _ID: PAYMENT_KEY,
              group: 'table',
              label: 'Forma de pagamento',
              value: attempt.payment_type.name,
            });
          }
        }

        fields.push({
          label: 'Valor do veículo',
          value: monetize(attempt.value_in_cents),
        }, {
          label: 'Valor da entrada',
          value: monetize(attempt.input_financing_in_cents),
        },
        ...details.installments,
        {
          label: 'Valor da documentação',
          value: monetize(attempt.value_of_the_license_plate_in_cents),
        }, {
          label: 'Valor de acessório',
          value: monetize(attempt.value_of_the_accessory_in_cents),
        });

        if (attempt.coefficient) {
          fields.push({
            separator: true,
            label: 'Coeficiente',
            value: monetize(attempt.coefficient, { prefix: '' }),
          });
        }

        if (attempt.first_payment_in_days) {
          fields.push({
            label: 'Carência',
            value: attempt.first_payment_in_days,
          });
        }

        if (_integrated) {
          if (this.unallowed[attempt.id].include_asset_insurance) {
            fields.push({
              label: BEM,
              value: undefined,
              tag: {
                label: UNALLOWED.include_asset_insurance(true, false),
                color: 'warning',
                icon: 'triangle-exclamation',
              },
            });
          }

          if (this.unallowed[attempt.id].include_personal_insurance) {
            fields.push({
              label: SAP,
              value: undefined,
              tag: {
                label: UNALLOWED.include_personal_insurance(true, false),
                color: 'warning',
                icon: 'triangle-exclamation',
              },
            });
          }

          if (this.unallowed[attempt.id].include_capitalization_bond) {
            fields.push({
              label: CAP,
              value: undefined,
              tag: {
                label: UNALLOWED.include_capitalization_bond(true, false),
                color: 'warning',
                icon: 'triangle-exclamation',
              },
            });
          }
        }

        fields.push(...details.insurances);

        if (_integrated) {
          fields.push(
            {
              label: 'Taxas da tabela',
              value: monetize(
                (attempt.table.tc || 0) + (attempt.table.expenses || [])
                  .reduce((total, i) => (total + i), 0),
              ),
            }, {
              label: 'Valor financiado',
              value: monetize(attempt.financed_amount_in_cents),
            },
            ...details.interests,
          );
        }

        const {
          commission_type: commissions,
        } = this.domainsWeb.list.items || {};

        if (commissions) {
          fields.push(...commissions.map(type => ({
            _ID: `COMMISSION-${type.identifier}`,
            group: 'commissions',
            label: `Comissão: ${type.name}`,
            value: (
              (attempt.bank_commissions || [])
                .find(i => i.commission_type === type.identifier) || {}
            ).value_cents,
          })));
        }

        if (_integrated) {
          fields.push({
            _ID: CODE_KEY,
            group: 'extra',
            label: 'Código de integração',
            value: attempt.bank_proposal_identifier,
          });
        }

        fields.push({
          _ID: NOTE_KEY,
          group: 'extra',
          label: 'Observação',
          value: attempt.obs,
        });

        return {
          _ID: attempt.id,
          _COLLAPSED: Object.values(this.attempts).length > 1,
          title: attempt.bank ? attempt.bank.name : 'Banco desconhecido',
          html: {
            id: `attempt-${attempt.id}`,
          },
          fields: fields.map(field => ({
            ...field,
            labelClass: isOutdated ? 'outdated-label' : '',
          })),
        };
      },

      tagged(proposal) {
        if (proposal) {
          return origin(proposal) || origin(proposal.proposal_attempt);
        }

        return null;
      },

      isUnallowed(attempt) {
        // eslint-disable-next-line no-underscore-dangle
        return isUnallowed(this.unallowed, { id: attempt._ID });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-read-information {
  }

  .proposals-read-information-updated {
    margin-bottom: var(--cordel-spacing-micro);
    display: block;
  }

  .proposals-information-online {
    margin-top: var(--cordel-spacing-nano);
    margin-left: var(--cordel-spacing-nano);
  }

  .proposals-read-information-accordion {
    padding: var(--cordel-spacing-blaster) 0 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-giga) 0 0;
    }
  }

  .proposals-read-information-outdated {
    color: var(--cordel-color-alert-danger);
  }

  .proposals-read-information-integrated {
    color: var(--cordel-color-alert-success-dark);
  }

  .proposals-read-information-non-integrated {
    color: var(--cordel-color-alert-danger-dark);
  }

  .proposals-read-label-outdated {
    background-color: var(--cordel-color-tag-danger);
    color: var(--cordel-color-constant-dark);
    margin-right: var(--cordel-spacing-xxxs);
  }

  .proposals-read-label-replaces {
    background-color: var(--cordel-color-tag-warning);
    color: var(--cordel-color-constant-dark);
    margin-right: var(--cordel-spacing-xxxs);
  }

  .proposals-read-information-banks-list {
    margin: var(--cordel-spacing-nano) 0 0 var(--cordel-spacing-nano);

    list-style: none;
  }

  .proposals-read-information-banks-item {
    &::before {
      content: '\2022';
      margin-right: var(--cordel-spacing-nano);
    }
  }
</style>
